import { ImageIcon, ImageOff, UploadIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useOrgsApi } from 'src/api/db/apiOrgs'
import { cleanUrl } from 'src/shared/utils/helpers'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button, Card } from 'src/shared/components/ui'
import { cn } from 'src/shared/components/ui/utils'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useHandleLogos } from 'src/shared/hooks/useHandleLogos'
import { useImageColors } from 'src/shared/hooks/useImageColors'
import { LogoColorDisplay } from './components/LogoColorDisplay'
import { COLOR_EXTRACTION_CONFIG } from './consts'
import { WebsiteUrlForm } from './components/WebsiteUrlForm'
import { LogoGrid } from './components/LogoGrid'

export const ArtworkPicker = ({
  selectedDesignUrl,
  onSelectDesignUrl,
  onColorCountChange,
  withTitle = true,
  withCard = true,
  allowSkip = false,
  companyUrl,
  org_id,
}) => {
  const { t } = useLocalization()
  const { updateOrganization } = useOrgsApi({})
  const [websiteUrlInput, setWebsiteUrlInput] = useState('')
  const [isSubmittingWebsiteUrl, setIsSubmittingWebsiteUrl] = useState(false)

  const {
    uploadedLogos,
    brandfetchUrls: imgUrls,
    refreshBrandfetch,
    isUploading,
    fileInputRef,
    handleLogoUpload,
    handleDeleteLogo,
  } = useHandleLogos({
    org_id,
    companyUrl,
    selectedLogoUrl: selectedDesignUrl,
    onLogoSelect: onSelectDesignUrl,
  })

  const onSelectDesignUrlRef = useRef(onSelectDesignUrl)
  onSelectDesignUrlRef.current = onSelectDesignUrl
  const didAutoSelectArtwork = useRef(false)

  // Default to the first available artwork when nothing is selected yet
  useEffect(() => {
    if (didAutoSelectArtwork.current) return
    if (selectedDesignUrl) {
      didAutoSelectArtwork.current = true
      return
    }
    const firstLogo = uploadedLogos[0]?.url ?? imgUrls[0]
    if (!firstLogo) return
    didAutoSelectArtwork.current = true
    onSelectDesignUrlRef.current(firstLogo)
  }, [selectedDesignUrl, uploadedLogos, imgUrls])

  const onColorCountChangeRef = useRef(onColorCountChange)
  onColorCountChangeRef.current = onColorCountChange

  const {
    colors,
    isLoading: isLoadingColors,
    colorCount,
  } = useImageColors({
    imageUrl: selectedDesignUrl,
    colorTolerance: COLOR_EXTRACTION_CONFIG.colorTolerance,
    maxColors: COLOR_EXTRACTION_CONFIG.maxColors,
    enabled: !!selectedDesignUrl,
  })

  // Notify parent when color count changes (ref for callback to avoid effect loop)
  useEffect(() => {
    const cb = onColorCountChangeRef.current
    if (cb && selectedDesignUrl && colorCount > 0 && !isLoadingColors) {
      cb(colorCount)
    }
  }, [colorCount, isLoadingColors, selectedDesignUrl])

  const handleSubmitWebsiteUrl = async () => {
    if (!websiteUrlInput.trim() || !org_id) return

    try {
      setIsSubmittingWebsiteUrl(true)
      const cleanedUrl = cleanUrl(websiteUrlInput.trim())

      await updateOrganization.mutateAsync({
        id: org_id,
        company_url: cleanedUrl,
      })

      refreshBrandfetch()
      setWebsiteUrlInput('')
    } catch (error) {
      console.error('Error updating website URL:', error)
      alert(
        t(
          'Failed to update website URL. Please try again.',
          'Kunde inte uppdatera webbplats-URL. Försök igen.'
        )
      )
    } finally {
      setIsSubmittingWebsiteUrl(false)
    }
  }

  const isSkipSelected = allowSkip && selectedDesignUrl === null

  const content = (
    <Column className="gap-2">
      <Row className="gap-2 items-center justify-between">
        {withTitle && (
          <Row className="items-center gap-2">
            <ImageIcon className="w-4 h-4" />
            <p className="text-sm">Artwork</p>
          </Row>
        )}

        <Button
          size="sm"
          onClick={(e) => {
            e.stopPropagation()
            fileInputRef.current?.click()
          }}
          disabled={isUploading}
        >
          <UploadIcon className="w-3 h-3 mr-1.5" />
          {t('Upload', 'Ladda upp')}
        </Button>
      </Row>

      {allowSkip && (
        <button
          type="button"
          onClick={() => onSelectDesignUrl(null)}
          className={cn(
            'w-full rounded-lg border-2 p-3 text-left transition-colors',
            isSkipSelected
              ? 'border-blue-500 bg-gradient-to-br from-blue-50/70 via-blue-100/70 to-blue-200/80'
              : 'border-gray-200 bg-gradient-to-br from-white via-gray-50 to-gray-100 hover:border-gray-300'
          )}
        >
          <Row className="items-center gap-3">
            <div
              className={cn(
                'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
                isSkipSelected ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'
              )}
            >
              <ImageOff className="h-5 w-5" />
            </div>
            <Column className="min-w-0 gap-0.5">
              <p className="text-sm font-medium text-gray-900">
                {t('No artwork yet', 'Ingen artwork ännu')}
              </p>
              <p className="text-xs text-muted-foreground">
                {t(
                  'Skip for quoting — add the logo later when ready.',
                  'Hoppa över för offerten — lägg till logotypen senare.'
                )}
              </p>
            </Column>
          </Row>
        </button>
      )}

      <Column>
        <LogoGrid
          uploadedLogos={uploadedLogos}
          brandfetchUrls={imgUrls}
          selectedDesignUrl={selectedDesignUrl}
          onSelectDesignUrl={onSelectDesignUrl}
          onDeleteLogo={handleDeleteLogo}
        />

        {selectedDesignUrl && (
          <LogoColorDisplay colors={colors} colorCount={colorCount} />
        )}
      </Column>

      {imgUrls.length === 0 && (
        <WebsiteUrlForm
          websiteUrlInput={websiteUrlInput}
          onWebsiteUrlInputChange={setWebsiteUrlInput}
          isSubmitting={isSubmittingWebsiteUrl}
          onSubmit={handleSubmitWebsiteUrl}
        />
      )}

      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept="image/*"
        onChange={handleLogoUpload}
      />
    </Column>
  )

  if (withCard) {
    return <Card>{content}</Card>
  }

  return content
}
